/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table } from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";

import { TaxTypeEnum, TaxTypeRequestEnum } from "@/enum/tax-history";
import { useFilterActions } from "../ducks/hooks/action";
import { useFilterState } from "../ducks/hooks/state";
import SelectFilters from "./select-filters";
import { Label } from "components/ui/label";
import { DataTableViewOptions } from "./data-table-view-options";

const taxTypeMap: Record<TaxTypeEnum, string> = {
  GNRE: "Gnre",
  SPED: "Sped",
  NFF: "Nff",
  NCM: "Ncm",
};

const taxTypeRequestMap: Record<TaxTypeRequestEnum, string> = {
  CREATION: "Criação",
  UPDATE: "Alteração",
  DELETE: "Exclusão",
};

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

type FilterKeys =
  | "tax_type_request"
  | "type"
  | "created_at"
  | "search"
  | "meta";

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const { updateFilter, clearFilters } = useFilterActions();
  const filterState = useFilterState();

  const [searchValue, setSearchValue] = useState(
    filterState.filters.search || ""
  );

  const debouncedSearchUpdate = useCallback(
    debounce((value: string) => {
      updateFilter("search", value);
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSearchUpdate(event.target.value);
  };

  useEffect(() => {
    setSearchValue(filterState.filters.search || "");
  }, [filterState.filters.search]);

  const handleFilterChange = (key: FilterKeys, value: string) => {
    updateFilter(key, value === "all" ? undefined : value);
  };

  return (
    <div className="flex flex-wrap items-center gap-4 w-full">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="search-input">Responsável</Label>
          <Input
            id="search-input"
            placeholder="Digite o nome do responsável..."
            value={searchValue}
            onChange={handleSearchChange}
            className="h-8 w-[150px] lg:w-[200px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="type-filter">Tipo de Solicitação</Label>
          <Select
            onValueChange={(value) => handleFilterChange("type", value)}
            value={filterState.filters.type || "all"}
          >
            <SelectTrigger id="type-filter" className="h-8 w-[150px] lg:w-[200px]">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(taxTypeMap).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="tax-type-filter">Tipo Fiscal</Label>
          <Select
            onValueChange={(value) =>
              handleFilterChange("tax_type_request", value)
            }
            value={filterState.filters.tax_type_request || "all"}
          >
            <SelectTrigger id="tax-type-filter" className="h-8 w-[150px] lg:w-[200px]">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(taxTypeRequestMap).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="created-at">Criado em:</Label>
          <SelectFilters />
        </div>

        {Object.keys(filterState.filters).length > 0 && (
          <Button variant="outline" onClick={clearFilters} className="h-8">
            <X />
            <span className="font-medium text-sm">Resetar Filtros</span>
          </Button>
        )}
      </div>

      <div className="ml-auto">
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
